import React, { useEffect, useRef, useState } from "react";

import {
  Code2,
  BrainCircuit,
  Bot,
  Layers3,
  ArrowUpRight,
  CheckCircle2,
  Target,
  Eye,
  Lightbulb,
  Users,
  ShieldCheck,
  Rocket,
  Zap,
  Globe2
} from "lucide-react";

import { Link } from "react-router-dom";

import "./About.css";


function About() {

  /* =====================================================
     SCROLL ANIMATION REFS
  ===================================================== */

  const storyRef = useRef(null);
  const missionRef = useRef(null);
  const capabilitiesRef = useRef(null);
  const technologyRef = useRef(null);
  const valuesRef = useRef(null);
  const thinkingRef = useRef(null);
  const workingRef = useRef(null);
  const ctaRef = useRef(null);


  /* =====================================================
     SCROLL ANIMATION STATE
  ===================================================== */

  const [visibleSections, setVisibleSections] = useState({
    story: false,
    mission: false,
    capabilities: false,
    technology: false,
    values: false,
    thinking: false,
    working: false,
    cta: false
  });


  /* =====================================================
     INTERSECTION OBSERVER
     10% SECTION VISIBILITY
  ===================================================== */

  useEffect(() => {

    const sections = [
      {
        ref: storyRef,
        key: "story"
      },
      {
        ref: missionRef,
        key: "mission"
      },
      {
        ref: capabilitiesRef,
        key: "capabilities"
      },
      {
        ref: technologyRef,
        key: "technology"
      },
      {
        ref: valuesRef,
        key: "values"
      },
      {
        ref: thinkingRef,
        key: "thinking"
      },
      {
        ref: workingRef,
        key: "working"
      },
      {
        ref: ctaRef,
        key: "cta"
      }
    ];


    const observer = new IntersectionObserver(
      (entries) => {

        entries.forEach((entry) => {

          if (entry.isIntersecting) {

            const sectionKey =
              entry.target.dataset.animationSection;


            setVisibleSections((previous) => ({
              ...previous,
              [sectionKey]: true
            }));


            observer.unobserve(entry.target);
          }

        });

      },
      {
        threshold: 0.1
      }
    );


    sections.forEach(({ ref, key }) => {

      if (ref.current) {

        ref.current.dataset.animationSection =
          key;

        observer.observe(ref.current);
      }

    });


    return () => {
      observer.disconnect();
    };

  }, []);


  /* =====================================================
     CAPABILITIES
  ===================================================== */

  const capabilities = [

    {
      icon: <Code2 size={23} />,
      title: "Software Development",
      text:
        "Modern web and software solutions built with scalable architecture, clean code and reliable performance."
    },

    {
      icon: <BrainCircuit size={23} />,
      title: "AI & Machine Learning",
      text:
        "Practical AI and machine learning solutions designed to turn complex data and workflows into useful products."
    },

    {
      icon: <Bot size={23} />,
      title: "AI Automation",
      text:
        "Intelligent workflows that reduce repetitive work, connect systems and improve operational efficiency."
    },

    {
      icon: <Layers3 size={23} />,
      title: "Full-Stack Systems",
      text:
        "Complete digital systems covering frontend experiences, backend APIs, databases and business logic."
    }

  ];


  /* =====================================================
     VALUES
  ===================================================== */

  const values = [

    {
      icon: <Lightbulb size={21} />,
      title: "Innovation",
      text:
        "We explore better ways to solve problems and create technology that delivers real value."
    },

    {
      icon: <Users size={21} />,
      title: "Collaboration",
      text:
        "We work closely with clients throughout the project to keep ideas, priorities and execution aligned."
    },

    {
      icon: <ShieldCheck size={21} />,
      title: "Integrity",
      text:
        "Clear communication, realistic expectations and responsible development guide every project."
    },

    {
      icon: <Rocket size={21} />,
      title: "Growth",
      text:
        "We build solutions with future improvements, scalability and long-term business growth in mind."
    },

    {
      icon: <Target size={21} />,
      title: "Purpose",
      text:
        "Every feature and technology choice should contribute to a clear business objective."
    },

    {
      icon: <Zap size={21} />,
      title: "Quality",
      text:
        "We focus on performance, usability, responsiveness and dependable implementation."
    }

  ];


  /* =====================================================
     TECHNOLOGY
  ===================================================== */

  const technologyFocus = [

    "React & Modern Frontend Development",
    "Node.js & REST API Development",
    "Java & Spring Boot",
    "MySQL & Database Systems",
    "AI & Machine Learning",
    "AI Workflow Automation",
    "Cloud & Deployment",
    "Business Process Automation"

  ];


  return (

    <div className="about-page">


      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="about-hero">

        <div className="about-hero-grid about-container">

          <div className="about-hero-content">

            <div className="about-label">

              <span></span>

              ABOUT NEXIS FOLD

            </div>


            <h1>

              We build technology

              <br />

              <span>
                that moves businesses forward.
              </span>

            </h1>


            <p>

              Nexis Fold is a technology development agency focused on
              building modern websites, applications, AI solutions and
              automation systems for businesses that want to move faster.

            </p>


            <div className="about-hero-actions">

              <Link
                to="/contact"
                className="about-primary-btn"
              >

                Start a Project

                <ArrowUpRight size={17} />

              </Link>


              <Link
                to="/projects"
                className="about-secondary-btn"
              >

                View Our Work

              </Link>

            </div>


            <div className="about-hero-stats">

              <div>
                <strong>06</strong>
                <span>Projects</span>
              </div>


              <div>
                <strong>04</strong>
                <span>Core Capabilities</span>
              </div>


              <div>
                <strong>100%</strong>
                <span>Focused on Quality</span>
              </div>

            </div>

          </div>


          {/* HERO VISUAL */}

          <div className="about-hero-visual">

            <div className="about-visual-header">

              <span>NEXIS FOLD</span>

              <span>ABOUT</span>

            </div>


            <div className="about-visual-main">

              <div className="about-visual-icon">

                <Code2
                  size={42}
                  strokeWidth={1.5}
                />

              </div>


              <h3>

                Build.
                <br />
                Automate.
                <br />
                Grow.

              </h3>


              <p>

                Practical technology for modern businesses.

              </p>

            </div>


            <div className="about-visual-footer">

              <span>01</span>

              <div></div>

              <span>
                TECHNOLOGY PARTNER
              </span>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          OUR STORY
      ===================================================== */}

      <section
        ref={storyRef}
        className={`about-story about-section ${
          visibleSections.story
            ? "about-section-visible"
            : ""
        }`}
      >

        <div className="about-container">

          <div className="about-story-content">


            <div className="about-story-heading about-reveal-left">

              <span className="about-section-label">
                OUR STORY
              </span>


              <h2>

                Technology should solve

                <span>
                  {" "}real problems.
                </span>

              </h2>

            </div>


            <div className="about-story-body">


              <div className="about-story-text about-reveal-right">

                <p>

                  Nexis Fold was created with a simple idea:
                  businesses should be able to access modern
                  technology without unnecessary complexity.

                </p>


                <p>

                  From business websites and custom software to
                  AI-powered systems and automation, we focus on
                  understanding the problem first and then building
                  the right solution around it.

                </p>


                <p>

                  Our approach combines product thinking, design
                  and engineering to create digital experiences
                  that are practical, scalable and ready for
                  real-world use.

                </p>

              </div>


              <div className="about-story-points">

                <div className="story-point-1">

                  <CheckCircle2 size={18} />

                  <span>
                    Business-focused solutions
                  </span>

                </div>


                <div className="story-point-2">

                  <CheckCircle2 size={18} />

                  <span>
                    Modern technology stack
                  </span>

                </div>


                <div className="story-point-3">

                  <CheckCircle2 size={18} />

                  <span>
                    Scalable architecture
                  </span>

                </div>


                <div className="story-point-4">

                  <CheckCircle2 size={18} />

                  <span>
                    Clear communication
                  </span>

                </div>

              </div>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          MISSION & VISION
      ===================================================== */}

      <section
        ref={missionRef}
        className={`about-mission about-section ${
          visibleSections.mission
            ? "about-section-visible"
            : ""
        }`}
      >

        <div className="about-container">


          <div className="about-section-heading centered about-reveal-up">

            <span className="about-section-label">
              WHAT DRIVES US
            </span>


            <h2>

              Built around a clear

              <span>
                {" "}mission & vision.
              </span>

            </h2>


            <p>

              We believe technology should be useful,
              accessible and designed around the people
              and businesses using it.

            </p>

          </div>


          <div className="mission-grid">


            <div className="mission-card mission-card-1">

              <div className="mission-icon">

                <Target size={23} />

              </div>


              <span>
                OUR MISSION
              </span>


              <h3>
                Turn ideas into useful digital products.
              </h3>


              <p>

                Our mission is to help businesses transform ideas,
                challenges and manual processes into reliable digital
                products that create measurable value.

              </p>

            </div>


            <div className="mission-card mission-card-2">

              <div className="mission-icon">

                <Eye size={23} />

              </div>


              <span>
                OUR VISION
              </span>


              <h3>
                Make technology a competitive advantage.
              </h3>


              <p>

                We envision a future where businesses of every size
                can use software, AI and automation to operate smarter,
                deliver better experiences and grow confidently.

              </p>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          CAPABILITIES
      ===================================================== */}

      <section
        ref={capabilitiesRef}
        className={`about-capabilities about-section ${
          visibleSections.capabilities
            ? "about-section-visible"
            : ""
        }`}
      >

        <div className="about-container">


          <div className="about-section-heading about-reveal-left">

            <span className="about-section-label">
              WHAT WE DO
            </span>


            <h2>

              Capabilities designed

              <span>
                {" "}for modern businesses.
              </span>

            </h2>


            <p>

              We bring development, AI and automation capabilities
              together to create complete digital solutions.

            </p>

          </div>


          <div className="capabilities-grid">

            {capabilities.map(
              (item, index) => (

                <div
                  className={`capability-card capability-card-${(
                    index % 4
                  ) + 1}`}
                  key={index}
                >

                  <div className="capability-card-top">

                    <div className="capability-icon">

                      {item.icon}

                    </div>


                    <span>

                      0{index + 1}

                    </span>

                  </div>


                  <h3>
                    {item.title}
                  </h3>


                  <p>
                    {item.text}
                  </p>


                  <Link to="/services">

                    Explore Capability

                    <ArrowUpRight size={15} />

                  </Link>

                </div>

              )

            )}

          </div>

        </div>

      </section>


      {/* =====================================================
          TECHNOLOGY
      ===================================================== */}

      <section
        ref={technologyRef}
        className={`about-technology about-section ${
          visibleSections.technology
            ? "about-section-visible"
            : ""
        }`}
      >

        <div className="about-container">

          <div className="technology-grid">


            <div className="technology-content about-reveal-left">

              <span className="about-section-label">
                TECHNOLOGY FOCUS
              </span>


              <h2>

                The right technology

                <span>
                  {" "}for the right problem.
                </span>

              </h2>


              <p>

                We choose technologies based on project requirements,
                scalability, performance and long-term maintainability
                rather than simply following trends.

              </p>


              <Link
                to="/services"
                className="about-outline-btn"
              >

                Explore Services

                <ArrowUpRight size={17} />

              </Link>

            </div>


            <div className="technology-list">

              {technologyFocus.map(
                (technology, index) => (

                  <div
                    className="technology-item"
                    key={index}
                  >

                    <span>

                      {String(index + 1).padStart(
                        2,
                        "0"
                      )}

                    </span>


                    <CheckCircle2 size={17} />


                    <strong>
                      {technology}
                    </strong>


                    <ArrowUpRight
                      size={14}
                      className="technology-arrow"
                    />

                  </div>

                )
              )}

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          VALUES
      ===================================================== */}

      <section
        ref={valuesRef}
        className={`about-values about-section ${
          visibleSections.values
            ? "about-section-visible"
            : ""
        }`}
      >

        <div className="about-container">


          <div className="about-section-heading centered about-reveal-up">

            <span className="about-section-label">
              OUR VALUES
            </span>


            <h2>

              Principles behind

              <span>
                {" "}our work.
              </span>

            </h2>


            <p>

              The way we work matters just as much
              as what we build.

            </p>

          </div>


          <div className="values-grid">

            {values.map(
              (value, index) => (

                <div
                  className={`value-card value-card-${(
                    index % 3
                  ) + 1}`}
                  key={index}
                >

                  <div className="value-icon">

                    {value.icon}

                  </div>


                  <div>

                    <h3>
                      {value.title}
                    </h3>


                    <p>
                      {value.text}
                    </p>

                  </div>

                </div>

              )
            )}

          </div>

        </div>

      </section>


      {/* =====================================================
          HOW WE THINK
      ===================================================== */}

      <section
        ref={thinkingRef}
        className={`about-thinking about-section ${
          visibleSections.thinking
            ? "about-section-visible"
            : ""
        }`}
      >

        <div className="about-container">

          <div className="thinking-box">


            <div className="thinking-content about-reveal-left">

              <span className="about-section-label">
                HOW WE THINK
              </span>


              <h2>

                Less complexity.

                <br />

                <span>
                  More meaningful technology.
                </span>

              </h2>


              <p>

                Great digital products are not created by adding more
                features. They are created by understanding what matters,
                removing unnecessary complexity and building experiences
                that people actually want to use.

              </p>


              <div className="thinking-points">


                <div className="thinking-point-1">

                  <div className="thinking-point-icon">

                    <Target size={17} />

                  </div>


                  <div>

                    <strong>
                      Understand first
                    </strong>


                    <span>
                      We begin with the business problem
                      and desired outcome.
                    </span>

                  </div>

                </div>


                <div className="thinking-point-2">

                  <div className="thinking-point-icon">

                    <Layers3 size={17} />

                  </div>


                  <div>

                    <strong>
                      Build with purpose
                    </strong>


                    <span>
                      Every component should have a clear
                      reason to exist.
                    </span>

                  </div>

                </div>


                <div className="thinking-point-3">

                  <div className="thinking-point-icon">

                    <Rocket size={17} />

                  </div>


                  <div>

                    <strong>
                      Think long term
                    </strong>


                    <span>
                      We consider scalability, maintenance
                      and future growth.
                    </span>

                  </div>

                </div>

              </div>

            </div>


            <div className="thinking-visual about-thinking-visual">

              <div className="thinking-visual-header">
                NEXIS FOLD
              </div>


              <div className="thinking-visual-center">

                <span>
                  N
                </span>

              </div>


              <div className="thinking-visual-footer">

                BUILD · AUTOMATE · GROW

              </div>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          WORKING MODEL
      ===================================================== */}

      <section
        ref={workingRef}
        className={`about-working about-section ${
          visibleSections.working
            ? "about-section-visible"
            : ""
        }`}
      >

        <div className="about-container">


          <div className="about-section-heading centered about-reveal-up">

            <span className="about-section-label">
              OUR APPROACH
            </span>


            <h2>

              A simple way to

              <span>
                {" "}work together.
              </span>

            </h2>


            <p>

              From the first conversation to product delivery,
              we keep communication direct and the process transparent.

            </p>

          </div>


          <div className="working-grid">


            <div className="working-card working-card-1">

              <span>01</span>

              <Globe2 size={21} />

              <h3>
                Understand
              </h3>

              <p>
                We learn about your business, users,
                challenges and goals.
              </p>

            </div>


            <div className="working-card working-card-2">

              <span>02</span>

              <Layers3 size={21} />

              <h3>
                Plan
              </h3>

              <p>
                We define the solution, scope, technology
                and development direction.
              </p>

            </div>


            <div className="working-card working-card-3">

              <span>03</span>

              <Code2 size={21} />

              <h3>
                Build
              </h3>

              <p>
                We design and develop the product
                with continuous feedback.
              </p>

            </div>


            <div className="working-card working-card-4">

              <span>04</span>

              <Rocket size={21} />

              <h3>
                Launch
              </h3>

              <p>
                We prepare the solution for real users
                and future growth.
              </p>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          FINAL CTA
      ===================================================== */}

      <section
        ref={ctaRef}
        className={`about-final-cta ${
          visibleSections.cta
            ? "about-section-visible"
            : ""
        }`}
      >

        <div className="about-container">

          <div className="about-cta-content">


            <span className="about-section-label">

              LET'S BUILD SOMETHING

            </span>


            <h2>

              Have an idea?

              <span>
                {" "}Let's turn it into reality.
              </span>

            </h2>


            <p>

              Tell us what you're building, what you're trying to solve,
              or where you want to go next. We'll help you figure out the
              right technology and approach.

            </p>


            <Link
              to="/contact"
              className="about-primary-btn"
            >

              Start a Conversation

              <ArrowUpRight size={17} />

            </Link>

          </div>

        </div>

      </section>


    </div>
  );
}


export default About;